import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    NativeSelect,
    Paper,
    Tab,
    Tabs,
    TextField,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import { catalogApi as api } from 'api/catalog';
import { partnershipsApi } from 'api/partnerships';
import { warehouseApi } from 'api/warehouse';
import { DataTable, FormComponent, Notifications, TabPanel } from 'shared';

import { stateOptions } from '../../../utils/globalUtils';
import { createMultiChipHandlers } from '../utils/itemInformationHelpers';

import { BulkUploadItemInformationPhotos } from './bulkUploadItemInformationPhotos';

function ItemInformation() {
    const [tags, setTags] = useState([]);
    const [selectedTab, setSelectedTab] = useState('0');
    const [importedOrderSources, setImportedOrderSources] = useState([]);

    const getTags = async () => {
        const res = await api.getItemInformationTags();
        if (res.status === true) {
            setTags(res.data.tags);
        }
    };

    const getImportedOrderSources = async () => {
        const res = await partnershipsApi.getSources();
        if (res.status) {
            setImportedOrderSources(res?.data?.rows);
        }
    };

    useEffect(() => {
        getTags();
        getImportedOrderSources();
    }, []);

    const handleChange = (event, value) => {
        setSelectedTab(value);
    };

    return (
        <Paper>
            <Tabs onChange={handleChange} value={selectedTab} indicatorColor="primary" textColor="primary" centered>
                <Tab id="Items_Tab" label="Items" value="0" />
                <Tab id="Tags_Tab" label="Tags" value="1" />
                <Tab id="UploadPhoto_Tab" label="Bulk Photo" value="2" />
            </Tabs>
            <TabPanel selectedTab={selectedTab} index="0">
                <ItemsComponent tags={tags} importedOrderSources={importedOrderSources} />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="1">
                <TagsComponent tags={tags} setTags={setTags} getTags={getTags} />
            </TabPanel>
            <TabPanel selectedTab={selectedTab} index="2">
                <BulkUploadItemInformationPhotos />
            </TabPanel>
        </Paper>
    );
}

function EditPriceOverridesModal({ facilities, getItemInformation, selectedRow, setNotification, setOpen }) {
    const facilitiesWithDefaultPrice = selectedRow.priceOverrides.map((item) => item.facilityID);
    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open
            onClose={() => {
                setOpen(false);
            }}
        >
            <DialogTitle>Edit Price Overrides - External Sku: {selectedRow.externalSku}</DialogTitle>
            <DialogContent>
                <DataTable
                    editable
                    columns={[
                        { accessor: 'facility', Header: 'Facility', editable: false },
                        {
                            accessor: 'defaultPrice',
                            Header: 'Default Price',
                            editable: true,
                            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0' },
                        },
                        {
                            Header: 'Remove',
                            Cell: ({ row }) => (
                                <Button
                                    id={`Remove_${row.id}`}
                                    onClick={async () => {
                                        const res = await api.updateDefaultPriceByExternalSkuFacilityID({
                                            externalSku: selectedRow.externalSku,
                                            facilityID: row.original.facilityID,
                                            defaultPrice: null,
                                        });
                                        if (!res.status) {
                                            setNotification({
                                                level: 'error',
                                                text: `Failed to update: ${res.msg}`,
                                            });
                                            return;
                                        }
                                        getItemInformation();
                                    }}
                                >
                                    Remove
                                </Button>
                            ),
                        },
                    ]}
                    data={selectedRow.priceOverrides}
                    saveEdit={async ({ row, value }) => {
                        const res = await api.updateDefaultPriceByExternalSkuFacilityID({
                            externalSku: selectedRow.externalSku,
                            facilityID: row.facilityID,
                            defaultPrice: value,
                        });
                        if (!res.status) {
                            setNotification({ level: 'error', text: `Failed to update: ${res.msg}` });
                            return false;
                        }
                        getItemInformation();
                        return true;
                    }}
                />

                <h3>Add Default Price For Facility</h3>
                <FormComponent
                    formFields={[
                        {
                            name: 'facilityID',
                            label: 'Facility',
                            inputElement: 'select',
                            inputProps: {
                                required: true,
                                opts: facilities
                                    .filter((item) => !facilitiesWithDefaultPrice.includes(item.id))
                                    .map((item) => ({ text: item.name, value: item.id })),
                            },
                        },
                        {
                            name: 'defaultPrice',
                            label: 'Default Price',
                            inputProps: { required: true, type: 'number' },
                        },
                    ]}
                    onSubmit={async (formData, resetForm) => {
                        const res = await api.updateDefaultPriceByExternalSkuFacilityID({
                            externalSku: selectedRow.externalSku,
                            facilityID: formData.facilityID,
                            defaultPrice: formData.defaultPrice,
                        });
                        if (!res.status) {
                            setNotification({ level: 'error', text: `Failed to create aisle mapping: ${res.msg}` });
                            return;
                        }
                        getItemInformation();
                        resetForm();
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function EditAisleModal({ getItemInformation, selectedAisle, setEditAisleModalOpen, setNotification, taxonomy }) {
    const getTaxonomyOptions = (row, level) => {
        const parentID = level > 1 ? row[`l${level - 1}AisleID`] : 0;
        const opts = taxonomy[`l${level}`]
            .filter((item) => item.parentID === Number(parentID))
            .map((item) => ({ text: item.name, value: item.id }));
        opts.unshift({ text: '', value: null });
        return opts;
    };

    const addAisleFields = [
        {
            name: 'l1AisleID',
            inputElement: 'select',
            label: 'Level 1 Aisle',
            gridValue: 3,
            inputProps: { required: true, opts: getTaxonomyOptions({}, 1) },
        },
        {
            name: 'l2AisleID',
            inputElement: 'select',
            label: 'Level 2 Aisle',
            gridValue: 3,
            dependencies: ['l1AisleID'],
            inputProps: {
                opts: (formData) => {
                    if (!formData.l1AisleID) {
                        return [];
                    }
                    return getTaxonomyOptions(formData, 2);
                },
            },
        },
        {
            name: 'l3AisleID',
            inputElement: 'select',
            label: 'Level 3 Aisle',
            gridValue: 3,
            dependencies: ['l1AisleID', 'l2AisleID'],
            inputProps: {
                opts: (formData) => {
                    if (!formData.l1AisleID || !formData.l2AisleID) {
                        return [];
                    }
                    return getTaxonomyOptions(formData, 3);
                },
            },
        },
        {
            name: 'l4AisleID',
            inputElement: 'select',
            label: 'Level 4 Aisle',
            gridValue: 3,
            dependencies: ['l1AisleID', 'l2AisleID', 'l3AisleID'],
            inputProps: {
                opts: (formData) => {
                    if (!formData.l1AisleID || !formData.l2AisleID || !formData.l3AisleID) {
                        return [];
                    }
                    return getTaxonomyOptions(formData, 4);
                },
            },
        },
    ];

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open
            onClose={() => {
                setEditAisleModalOpen(false);
            }}
        >
            <DialogTitle>Edit Aisle Taxonomy - External Sku: {selectedAisle.externalSku}</DialogTitle>
            <DialogContent>
                <DataTable
                    editable
                    columns={[
                        {
                            accessor: 'l1AisleID',
                            Header: 'L1 Aisle',
                            editable: true,
                            editProps: {
                                type: 'select',
                                options: (row) => getTaxonomyOptions(row, 1),
                            },
                        },
                        {
                            accessor: 'l2AisleID',
                            Header: 'L2 Aisle',
                            editable: true,
                            editProps: {
                                type: 'select',
                                options: (row) => getTaxonomyOptions(row, 2),
                            },
                        },
                        {
                            accessor: 'l3AisleID',
                            Header: 'L3 Aisle',
                            editable: true,
                            editProps: {
                                type: 'select',
                                options: (row) => getTaxonomyOptions(row, 3),
                            },
                        },
                        {
                            accessor: 'l4AisleID',
                            Header: 'L4 Aisle',
                            editable: true,
                            editProps: {
                                type: 'select',
                                options: (row) => getTaxonomyOptions(row, 4),
                            },
                        },
                        {
                            Header: 'Remove',
                            Cell: ({ row }) => (
                                <Button
                                    id={`Remove_${row.id}`}
                                    onClick={async () => {
                                        const res = await api.deleteExternalSkuAisleMapping(row.original.id);
                                        if (!res.status) {
                                            setNotification({
                                                level: 'error',
                                                text: `Failed to remove aisle mapping: ${res.msg}`,
                                            });
                                            return;
                                        }
                                        getItemInformation();
                                    }}
                                >
                                    Remove
                                </Button>
                            ),
                        },
                    ]}
                    data={selectedAisle.aisles}
                    saveEdit={async ({ field, id, value }) => {
                        const res = await api.editExternalSkuAisleMapping(id, { [field]: value });
                        if (!res.status) {
                            setNotification({ level: 'error', text: `Failed to edit aisle mapping: ${res.msg}` });
                            return false;
                        }
                        getItemInformation();
                        return true;
                    }}
                />

                <h3>Add Aisle Mapping</h3>
                <FormComponent
                    formFields={addAisleFields}
                    onSubmit={async (formData, resetForm) => {
                        formData.externalSku = selectedAisle.externalSku;
                        const res = await api.createExternalSkuAisleMapping(formData);
                        if (!res.status) {
                            setNotification({ level: 'error', text: `Failed to create aisle mapping: ${res.msg}` });
                            return;
                        }
                        getItemInformation();
                        resetForm();
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function PhotoModal({ selectedPhoto, setPhotoModalOpen, updateItemPhoto }) {
    const [file, setFile] = useState(null);
    const [source, setSource] = useState(null);
    const [title, setTitle] = useState('');
    const [photoType, setPhotoType] = useState('');
    const [loading, setLoading] = useState(false);

    const { item } = selectedPhoto;
    const { key } = selectedPhoto;

    useEffect(() => {
        if (key === 'photoURLNutritionalInfo') {
            setTitle(`Edit Nutritional Info Image for ${item.name}`);
            setPhotoType('nutritional');
        } else if (key === 'photoTwoURLSmall') {
            setTitle(`Edit Photo 2 for ${item.name}`);
            setPhotoType('photo2');
        } else if (key === 'photoThreeURLSmall') {
            setTitle(`Edit Photo 3 for ${item.name}`);
            setPhotoType('photo3');
        } else if (key === 'photoFourURLSmall') {
            setTitle(`Edit Photo 4 for ${item.name}`);
            setPhotoType('photo4');
        } else if (key === 'photoFiveURLSmall') {
            setTitle(`Edit Photo 5 for ${item.name}`);
            setPhotoType('photo5');
        } else {
            setPhotoType('product');
            setTitle(`Edit Product Image for ${item.name}`);
        }
    }, []);

    return (
        <Dialog
            open
            onClose={() => {
                setPhotoModalOpen(false);
            }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {item[key] && !source && <img src={`https://static.misfitsmarket.com/${item[key]}`} alt="Item" />}
                {source && (
                    <div style={{ textAlign: 'center' }}>
                        <img src={source} alt="Preview" />
                    </div>
                )}
                <Button id="ChooseFile" style={{ textAlign: 'center' }} variant="outlined" component="label">
                    Choose File
                    <input
                        style={{ display: 'none' }}
                        type="file"
                        name="Photo Upload"
                        id="photo-file-input"
                        accept="image/png, image/jpeg"
                        onChange={(event) => {
                            const file = event.target.files[0];
                            setFile(file);

                            const reader = new FileReader();
                            reader.readAsDataURL(file);
                            reader.onload = (e) => {
                                setSource(e.target.result);
                            };
                        }}
                    />
                </Button>
            </DialogContent>
            <DialogActions>
                {(key === 'photoURLNutritionalInfo' ||
                    key == 'photoTwoURLSmall' ||
                    key == 'photoThreeURLSmall' ||
                    key == 'photoFourURLSmall' ||
                    key == 'photoFiveURLSmall') &&
                    !!item[key] && (
                        <Button
                            id="RemovePhoto"
                            color="secondary"
                            disabled={loading === true}
                            onClick={async () => {
                                setLoading(true);
                                const res = await updateItemPhoto(photoType, '', item.externalSku);
                                setLoading(false);
                                if (res === true) {
                                    setPhotoModalOpen(false);
                                }
                            }}
                        >
                            Remove Photo
                        </Button>
                    )}
                <Button
                    id="Confirm"
                    disabled={file === null || loading === true}
                    onClick={async () => {
                        setLoading(true);
                        const res = await updateItemPhoto(photoType, file, item.externalSku);
                        setLoading(false);
                        if (res === true) {
                            setPhotoModalOpen(false);
                        }
                    }}
                >
                    Confirm
                </Button>
                <Button id="Cancel" disabled={loading} onClick={() => setPhotoModalOpen(false)}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function PriceOverridesCell({ row, setEditPriceOverridesModalOpen, setSelectedRow }) {
    const { priceOverrides } = row.original;

    return (
        <div>
            {priceOverrides.map((item, index) => (
                <div key={`${index}`}>
                    <span>
                        {item.facility} - {item.defaultPrice}
                    </span>
                </div>
            ))}
            <div>
                <Button
                    id={`PriceOverride_${row.id}`}
                    onClick={() => {
                        setSelectedRow(row.original);
                        setEditPriceOverridesModalOpen(true);
                    }}
                >
                    EDIT
                </Button>
            </div>
        </div>
    );
}

function AislesCell({ row, setEditAisleModalOpen, setSelectedRow, taxonomy }) {
    const { aisles } = row.original;

    return (
        <div>
            {aisles.map((item, index) => (
                <div key={`${index}`} style={{ paddingTop: '2px' }}>
                    {!!item.l1AisleID && (
                        <span style={{ display: 'inline' }}>
                            {taxonomy?.l1?.find((tx) => tx.id === item.l1AisleID)?.name}
                        </span>
                    )}
                    {!!item.l2AisleID && (
                        <span style={{ display: 'inline' }}>
                            {'-> '}
                            {taxonomy?.l2?.find((tx) => tx.id === item.l2AisleID)?.name}
                        </span>
                    )}
                    {!!item.l3AisleID && (
                        <span style={{ display: 'inline' }}>
                            {'-> '}
                            {taxonomy?.l3?.find((tx) => tx.id === item.l3AisleID)?.name}
                        </span>
                    )}
                    {!!item.l4AisleID && (
                        <span style={{ display: 'inline' }}>
                            {'-> '}
                            {taxonomy?.l4?.find((tx) => tx.id === item.l4AisleID)?.name}
                        </span>
                    )}
                </div>
            ))}
            <div>
                <Button
                    id={`EditAisles_${row.id}`}
                    onClick={() => {
                        setSelectedRow(row.original);
                        setEditAisleModalOpen(true);
                    }}
                >
                    EDIT
                </Button>
            </div>
        </div>
    );
}

function PhotoCell({ column, row, setPhotoModalOpen, setSelectedPhoto }) {
    const key = column.id;
    if (row.original[key]) {
        return (
            <div>
                <Button
                    id={`${column.Header.replaceAll(' ', '')}_${row.id}`}
                    onClick={() => {
                        setSelectedPhoto({ item: row.original, key });
                        setPhotoModalOpen(true);
                    }}
                >
                    <img
                        src={`https://static.misfitsmarket.com/${row.original[key]}`}
                        style={{ height: '100px', width: '100px' }}
                    />
                </Button>
            </div>
        );
    }
    return (
        <div>
            <IconButton
                id={`${column.Header.replaceAll(' ', '')}_${row.id}_upload`}
                onClick={() => {
                    setSelectedPhoto({ item: row.original, key });
                    setPhotoModalOpen(true);
                }}
            >
                <CloudUploadIcon />
            </IconButton>
        </div>
    );
}

// Defines the Prop65 table cell
function Prop65Cell({ row, setEditProp65ModalOpen, setSelectedRow }) {
    return (
        <div>
            <span style={{ display: 'inline' }}>{row.original.prop65Warning ? 'Yes' : 'No'}</span>
            <Button
                id={`EditProp65_${row.id}`}
                onClick={() => {
                    setSelectedRow(row.original);
                    setEditProp65ModalOpen(true);
                }}
            >
                EDIT
            </Button>
        </div>
    );
}

// Modal for opening Prop 65
function EditProp65Modal({ getItemInformation, selectedItem, setEditProp65ModalOpen, setNotification }) {
    const [rows, setRows] = useState([]);
    const [prop65Flag, setProp65Flag] = useState(selectedItem.prop65Warning);
    const [alcoholFlag, setAlcoholFlag] = useState(selectedItem.prop65WarningCategoryAlcohol);
    const [cannedFlag, setCannedFlag] = useState(selectedItem.prop65WarningCategoryCanned);
    const [foodFlag, setFoodFlag] = useState(selectedItem.prop65WarningCategoryFood);
    const [nonFoodFlag, setNonFoodFlag] = useState(selectedItem.prop65WarningCategoryNonFood);
    const [ingredients, setIngredients] = useState([]);
    const [selectedIngredient, setSelectedIngredient] = useState({});

    const getIngredients = async () => {
        const res = await api.getPropSixtyFiveIngredientsBySKU(selectedItem.externalSku);
        if (res.status === true) {
            setRows(res.data.rows);
        }
    };

    const getAllIngredients = async () => {
        const res = await api.getPropSixtyFiveIngredients();
        if (res.status === true) {
            const pleaseSelect = { name: 'Please Select', value: 0 };
            setIngredients([pleaseSelect].concat(res.data.rows));
        }
    };

    const finishedEditing = () => {
        setEditProp65ModalOpen(false);
        getItemInformation();
    };

    const columns = [
        { accessor: 'id', Header: 'ID' },
        { accessor: 'name', Header: 'Name' },
        {
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <Button
                    onClick={async () => {
                        const response = await api.removePropSixtyFiveIngredient(selectedItem.id, row.original.id);

                        if (response.status === false) {
                            setNotification({ text: `Can't remove item ${response.msg}`, level: 'error' });
                            return;
                        }

                        getIngredients();
                    }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    useEffect(() => {
        getIngredients();
        getAllIngredients();
    }, []);

    useEffect(() => {
        setAlcoholFlag(selectedItem.prop65WarningCategoryAlcohol);
        setCannedFlag(selectedItem.prop65WarningCategoryCanned);
        setFoodFlag(selectedItem.prop65WarningCategoryFood);
        setNonFoodFlag(selectedItem.prop65WarningCategoryNonFood);
    }, [selectedItem]);

    return (
        <Dialog fullWidth maxWidth="md" open onClose={() => finishedEditing()}>
            <DialogTitle>Edit Prop 65 - External Sku: {selectedItem.externalSku}</DialogTitle>
            <DialogContent>
                <div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="Prop65Warning_checkbox"
                                onChange={async (event) => {
                                    setProp65Flag(event.target.checked);
                                    if (event.target.checked === false) {
                                        setAlcoholFlag(false);
                                        setCannedFlag(false);
                                        setFoodFlag(false);
                                        setNonFoodFlag(false);
                                    }
                                    const res = await api.updatePropSixtyFive(selectedItem.id, {
                                        prop65Warning: event.target.checked,
                                    });
                                    if (!res.status) {
                                        setNotification({
                                            level: 'error',
                                            text: `Failed to update prop65 flag: ${res.msg}`,
                                        });
                                    }
                                }}
                                color="primary"
                                defaultChecked={prop65Flag}
                            />
                        }
                        label="Prop65 Warning?"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="AlcoholCategory_checkbox"
                                onChange={async (event) => {
                                    setAlcoholFlag(event.target.checked);
                                    const res = await api.updatePropSixtyFive(selectedItem.id, {
                                        alcoholFlag: event.target.checked,
                                    });
                                    if (!res.status) {
                                        setNotification({
                                            level: 'error',
                                            text: `Failed to update alcohol flag: ${res.msg}`,
                                        });
                                    }
                                }}
                                color="primary"
                                defaultChecked={alcoholFlag}
                                checked={alcoholFlag}
                                disabled={!prop65Flag}
                            />
                        }
                        label="Alcohol Category"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                id="CannedBottledCategory_checkbox"
                                onChange={async (event) => {
                                    setCannedFlag(event.target.checked);
                                    const res = await api.updatePropSixtyFive(selectedItem.id, {
                                        cannedFlag: event.target.checked,
                                    });
                                    if (!res.status) {
                                        setNotification({
                                            level: 'error',
                                            text: `Failed to update canned flag: ${res.msg}`,
                                        });
                                    }
                                }}
                                color="primary"
                                defaultChecked={cannedFlag}
                                checked={cannedFlag}
                                disabled={!prop65Flag}
                            />
                        }
                        label="Canned/Bottled Category"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                id="FoodCategory_checkbox"
                                onChange={async (event) => {
                                    setFoodFlag(event.target.checked);
                                    const res = await api.updatePropSixtyFive(selectedItem.id, {
                                        foodFlag: event.target.checked,
                                    });
                                    if (!res.status) {
                                        setNotification({
                                            level: 'error',
                                            text: `Failed to update food flag: ${res.msg}`,
                                        });
                                    }
                                }}
                                color="primary"
                                defaultChecked={foodFlag}
                                checked={foodFlag}
                                disabled={!prop65Flag}
                            />
                        }
                        label="Food Category"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                id="NonFoodCategory_checkbox"
                                onChange={async (event) => {
                                    setNonFoodFlag(event.target.checked);
                                    const res = await api.updatePropSixtyFive(selectedItem.id, {
                                        nonFoodFlag: event.target.checked,
                                    });
                                    if (!res.status) {
                                        setNotification({
                                            level: 'error',
                                            text: `Failed to update nonFood flag: ${res.msg}`,
                                        });
                                    }
                                }}
                                color="primary"
                                defaultChecked={nonFoodFlag}
                                checked={nonFoodFlag}
                                disabled={!prop65Flag}
                            />
                        }
                        label="Non-Food Category"
                    />
                </div>
                <div>
                    <DataTable title="Ingredients" columns={columns} data={rows} />
                </div>
                <div>
                    <Button
                        id="AddIngredient"
                        onClick={async () => {
                            const res = await api.addPropSixtyFiveIngredient(selectedItem.id, selectedIngredient);
                            if (!res.status) {
                                setNotification({
                                    level: 'error',
                                    text: `Failed to add prop65 ingredient: ${res.msg}`,
                                });
                                return;
                            }
                            setNotification({ level: 'success', text: `Added ingredient` });
                            getIngredients();
                        }}
                    >
                        Add Ingredient
                    </Button>
                    <NativeSelect
                        id="Ingredients_select"
                        className="input-element-select"
                        value={selectedIngredient}
                        onChange={(event) => {
                            setSelectedIngredient(event.target.value);
                        }}
                        label="Ingredient"
                    >
                        {ingredients.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                                {opt.name}
                            </option>
                        ))}
                        ;
                    </NativeSelect>
                </div>
            </DialogContent>
            <DialogActions>
                <Button id="Done" onClick={() => finishedEditing()}>
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ItemsComponent({ importedOrderSources, tags }) {
    const [itemInformation, setItemInformation] = useState([]);
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState({});
    const [editAisleModalOpen, setEditAisleModalOpen] = useState(false);
    const [editProp65ModalOpen, setEditProp65ModalOpen] = useState(false);
    const [editPriceOverridesModalOpen, setEditPriceOverridesModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [notification, setNotification] = useState({ text: null, level: null });
    const [taxonomy, setTaxonomy] = useState({ l1: [], l2: [], l3: [], l4: [] });
    const [activeOnly, setActiveOnly] = useState(true);
    const [excludeRecipeCards, setExcludeRecipeCards] = useState(true);
    const [segments, setSegments] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(true);
    const latestFetchIDRef = useRef(0);

    const getItemInformation = async () => {
        // The catalog arrives over several sequential pages, so two of these can be in flight at once —
        // the edit modals stay mounted and keep calling this while the table is behind the spinner. Only
        // the newest fetch may write state, or an older one finishing later would win and leave the table
        // showing data the user did not ask for.
        const fetchID = latestFetchIDRef.current + 1;
        latestFetchIDRef.current = fetchID;

        setIsLoadingItems(true);
        try {
            const res = await api.getItemInformation(activeOnly, '', { includeRecipeCards: !excludeRecipeCards });

            if (fetchID !== latestFetchIDRef.current) {
                return;
            }

            if (res.status !== true) {
                // Clear rather than leave the previous filter's rows on screen: the checkboxes already show
                // the new activeOnly/excludeRecipeCards values, so keeping the old rows would present stale
                // data as if it matched them. Matches how catalog.jsx handles the same failure.
                setItemInformation([]);
                setNotification({ text: `Error loading items: ${res.msg}`, level: 'error' });
                return;
            }

            setItemInformation(
                res.data.items.map((item) => {
                    item.id = item.externalSku;

                    // refresh selected aisle data if it exists
                    if (selectedRow && selectedRow.hasOwnProperty('externalSku')) {
                        if (selectedRow.externalSku === item.externalSku) {
                            setSelectedRow(item);
                        }
                    }

                    item.tags = item.tags.map((tag) => ({ ...tag, text: tag.name, value: tag.id }));

                    return item;
                }),
            );
        } finally {
            // Leave the spinner up if a newer fetch is still running.
            if (fetchID === latestFetchIDRef.current) {
                setIsLoadingItems(false);
            }
        }
    };

    const saveEdit = async ({ field, id, value }) => {
        const externalSku = id;
        const formData = new FormData();
        formData.append(field, value);
        formData.append('externalSku', externalSku);
        const res = await api.updateItemInformation(formData);
        if (res.status === true) {
            // Show success alert when update succeeds
            setNotification({ text: 'Item updated!', level: 'success' });
        } else {
            setNotification({ text: res.msg ? res.msg : 'Error updating item', level: 'error' });
        }
        getItemInformation();
        return res.status;
    };

    const updateItemPhoto = async (type, file, externalSku) => {
        const formData = new FormData();
        formData.append(`${type}Photo`, file);
        formData.append('externalSku', externalSku);
        const res = await api.updateItemInformation(formData);
        if (res.status === true) {
            getItemInformation();
        } else {
            setNotification({ text: `Error uploading photo: ${res.msg}`, level: 'error' });
        }
        return res.status;
    };

    const getTaxonomy = async () => {
        const promises = [1, 2, 3, 4].map((lvl) => api.getTaxonomyByLevel(lvl, 'AISLE'));
        const res = await Promise.all(promises);
        if (res.every((item) => item.status)) {
            setTaxonomy({
                l1: res[0].data.categories,
                l2: res[1].data.categories,
                l3: res[2].data.categories,
                l4: res[3].data.categories,
            });
        }
    };

    const getSegments = async () => {
        const res = await api.getItemInformationSegments();
        if (res.status === true) {
            setSegments(res.data.rows);
        }
    };

    const getTiers = async () => {
        const res = await api.getItemInformationTiers();
        if (res.status === true) {
            setTiers(res.data.rows);
        }
    };

    const getFacilities = async () => {
        const response = await warehouseApi.getFacilities(true);
        if (response.status === false) {
            return;
        }

        setFacilities(response.data.rows);
    };

    useEffect(() => {
        getTaxonomy();
        getSegments();
        getTiers();
        getFacilities();
    }, []);

    useEffect(() => {
        getItemInformation();
    }, [activeOnly, excludeRecipeCards]);

    const columns = [
        { accessor: 'name', Header: 'Item Name', editable: true, editProps: { type: 'input', multiline: true } },
        { accessor: 'externalSku', Header: 'External Sku', editable: false },
        { accessor: 'ifNetsuiteID', Header: '(IF) Netsuite ID', editable: true },
        { accessor: 'dbiOnly', Header: 'Fulfillable by DBI only', type: 'checkbox', editable: true },
        {
            accessor: 'isOutOfBox',
            Header: 'Shipped Out Of Box?',
            type: 'checkbox',
            editable: true,
            editProps: { type: 'checkbox' },
        },
        { Header: 'Type', accessor: 'type' },
        {
            accessor: 'description',
            Header: 'Description',
            editable: true,
            editProps: { type: 'input', multiline: true },
            width: '100px',
        },
        {
            accessor: 'photoURLSmall',
            width: '125px',
            Header: 'Product Photo',
            editable: true,
            Cell: ({ column, row, saveEdit }) =>
                PhotoCell({ selectedPhoto, setSelectedPhoto, row, column, setPhotoModalOpen }),
        },
        {
            accessor: 'photoURLNutritionalInfo',
            width: '100px',
            Header: 'Nutrition',
            editable: true,
            Cell: ({ column, row, saveEdit }) =>
                PhotoCell({ selectedPhoto, setSelectedPhoto, row, column, setPhotoModalOpen }),
        },
        { accessor: 'allergens', Header: 'Allergens', editable: true, editProps: { type: 'input', multiline: true } },
        {
            accessor: 'crossAllergens',
            Header: 'Cross allergens (may contain)',
            editable: true,
            editProps: { type: 'input', multiline: true },
        },
        {
            accessor: 'ingredients',
            Header: 'Ingredients',
            width: '150px',
            editable: true,
            editProps: { type: 'input', multiline: true },
        },
        { accessor: 'brand', Header: 'Brand', editable: true },
        {
            accessor: 'tags',
            Header: 'Tags',
            editable: true,
            filterValues: (row) => row.original.tags.map((tag) => tag.name),
            editProps: {
                type: 'autocomplete-multiple-chips',
                options: tags.map((item) => ({ text: item.name, value: item.id })),
                modalHeader: (row) => `Add Tags to Item ${row.name}`,
                addButtonText: 'Add Tags',
                addItem: async ({ row, values }) => {
                    const res = await api.createItemInformationTagMappings(values, row.externalSku);
                    if (res.status === true) {
                        const itemInfos = [...itemInformation].map((itemInfo) => {
                            if (itemInfo.externalSku === row.externalSku) {
                                const selectedTagObjs = tags.filter((tag) => values.includes(tag.id));
                                itemInfo.tags = itemInfo.tags
                                    .concat(selectedTagObjs)
                                    .map((tag) => ({ ...tag, text: tag.name, value: tag.id }));
                            }
                            return itemInfo;
                        });
                        setItemInformation(itemInfos);
                    } else {
                        setNotification({ level: 'error', text: res.msg });
                    }
                    return true;
                },
                removeItem: async ({ item, row }) => {
                    const res = await api.removeItemInformationTagMapping(item.id, row.externalSku);
                    if (res.status === true) {
                        const itemInfos = [...itemInformation].map((itemInfo) => {
                            if (itemInfo.externalSku === row.externalSku) {
                                itemInfo.tags = itemInfo.tags.filter((tag) => tag.id !== item.id);
                            }
                            return itemInfo;
                        });
                        setItemInformation(itemInfos);
                    }
                },
            },
        },
        {
            Header: 'Aisles',
            width: '200px',
            Cell: ({ column, row, saveEdit }) => AislesCell({ row, taxonomy, setSelectedRow, setEditAisleModalOpen }),
        },
        {
            accessor: 'photoTwoURLSmall',
            width: '125px',
            Header: 'Product Photo 2',
            editable: true,
            Cell: ({ column, row, saveEdit }) =>
                PhotoCell({ selectedPhoto, setSelectedPhoto, row, column, setPhotoModalOpen }),
        },
        {
            accessor: 'photoThreeURLSmall',
            width: '125px',
            Header: 'Product Photo 3',
            editable: true,
            Cell: ({ column, row, saveEdit }) =>
                PhotoCell({ selectedPhoto, setSelectedPhoto, row, column, setPhotoModalOpen }),
        },
        {
            accessor: 'photoFourURLSmall',
            width: '125px',
            Header: 'Product Photo 4',
            editable: true,
            Cell: ({ column, row, saveEdit }) =>
                PhotoCell({ selectedPhoto, setSelectedPhoto, row, column, setPhotoModalOpen }),
        },
        {
            accessor: 'photoFiveURLSmall',
            width: '125px',
            Header: 'Product Photo 5',
            editable: true,
            Cell: ({ column, row, saveEdit }) =>
                PhotoCell({ selectedPhoto, setSelectedPhoto, row, column, setPhotoModalOpen }),
        },
        {
            accessor: 'msrpPrice',
            Header: 'Base Price',
            editable: true,
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0' },
        },
        { accessor: 'vanityPadName', Header: 'Vanity Pad Name', editable: true },
        {
            Header: 'Prop65',
            width: '150px',
            Cell: ({ column, row, saveEdit }) => Prop65Cell({ row, setEditProp65ModalOpen, setSelectedRow }),
        },
        { accessor: 'segment', Header: 'Segment', editable: true, editProps: { type: 'select', options: segments } },
        {
            accessor: 'priceOverrides',
            Header: 'Price Overrides',
            filterValues: (row) => row.original.priceOverrides.map((item) => `${item.facility} - ${item.defaultPrice}`),
            Cell: ({ row }) => PriceOverridesCell({ row, setSelectedRow, setEditPriceOverridesModalOpen }),
        },
        {
            accessor: 'priceSensitive',
            Header: 'Price Sensitive',
            type: 'checkbox',
            editable: true,
            editProps: { type: 'checkbox' },
        },
        { accessor: 'lOneTaxonomy', Header: 'L1 Taxonomy' },
        { accessor: 'lTwoTaxonomy', Header: 'L2 Taxonomy' },
        { accessor: 'lThreeTaxonomy', Header: 'L3 Taxonomy' },
        { accessor: 'lFourTaxonomy', Header: 'L4 Taxonomy' },
        { accessor: 'maxQuantity', Header: 'Max Quantity', editable: true },
        { accessor: 'headSku', Header: 'Head Sku', type: 'checkbox', editable: true, editProps: { type: 'checkbox' } },
        {
            accessor: 'acquisitionPdp',
            Header: 'Acquisition  PDP',
            type: 'checkbox',
            editable: true,
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'importedOrderSourceID',
            Header: 'FAM Partner',
            editable: true,
            editProps: {
                type: 'select',
                options: [
                    { id: null, name: '' },
                    ...importedOrderSources.map((item) => ({ id: item.id, name: item.name })),
                ],
            },
        },
        {
            accessor: 'tier',
            Header: 'Tier',
            editable: true,
            editProps: {
                type: 'select',
                options: tiers,
            },
        },
        {
            accessor: 'isSnapEligible',
            Header: 'Snap Eligible',
            editable: true,
            type: 'checkbox',
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'snapStateExceptions',
            Header: 'SNAP state exceptions',
            editable: true,
            editProps: {
                type: 'autocomplete-multiple-chips',
                options: stateOptions.map((option) => option.value),
                modalHeader: (row) => `Add SNAP state exception to ${row.id} - ${row.name}`,
                addButtonText: 'Add SNAP state exception',
                ...createMultiChipHandlers({
                    fieldName: 'snapStateExceptions',
                    setItemInformation,
                    setNotification,
                }),
            },
        },
    ];
    return (
        <Grid container>
            <Notifications options={notification} />
            {photoModalOpen && (
                <PhotoModal
                    setPhotoModalOpen={setPhotoModalOpen}
                    selectedPhoto={selectedPhoto}
                    updateItemPhoto={updateItemPhoto}
                />
            )}
            {editAisleModalOpen && (
                <EditAisleModal
                    setEditAisleModalOpen={setEditAisleModalOpen}
                    selectedAisle={selectedRow}
                    taxonomy={taxonomy}
                    getItemInformation={getItemInformation}
                    setNotification={setNotification}
                />
            )}
            {editProp65ModalOpen && (
                <EditProp65Modal
                    setEditProp65ModalOpen={setEditProp65ModalOpen}
                    selectedItem={selectedRow}
                    getItemInformation={getItemInformation}
                    setNotification={setNotification}
                />
            )}
            {editPriceOverridesModalOpen && (
                <EditPriceOverridesModal
                    setOpen={setEditPriceOverridesModalOpen}
                    setNotification={setNotification}
                    selectedRow={selectedRow}
                    getItemInformation={getItemInformation}
                    facilities={facilities}
                />
            )}
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="ActiveOnly_checkbox"
                            onChange={(event) => {
                                setActiveOnly(event.target.checked);
                            }}
                            color="primary"
                            defaultChecked={activeOnly}
                            // Toggling mid-fetch would start a second multi-page load racing the first,
                            // and whichever finished last would win — leaving the table showing the
                            // other filter's data.
                            disabled={isLoadingItems}
                        />
                    }
                    label="Active Only"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            id="ExcludeRecipeCards_checkbox"
                            onChange={(event) => {
                                setExcludeRecipeCards(event.target.checked);
                            }}
                            color="primary"
                            defaultChecked={excludeRecipeCards}
                            disabled={isLoadingItems}
                        />
                    }
                    label="Exclude Recipe Cards"
                />
            </Grid>
            {/* Only swap the table out on the very first load. A refetch after an edit must leave the table
                mounted: react-table holds the column filters, sort and page internally, so unmounting it
                would drop the user's search and send them back to the full catalog. */}
            {isLoadingItems && itemInformation.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" width="100%">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {isLoadingItems && <LinearProgress />}
                    <DataTable
                        editable
                        title="Item Information"
                        columns={columns}
                        data={itemInformation}
                        saveEdit={saveEdit}
                    />
                </>
            )}
        </Grid>
    );
}

function AddTagModal({ createTag, setAddTagModalOpen }) {
    const [name, setName] = useState('');
    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open
            onClose={() => {
                setAddTagModalOpen(false);
            }}
        >
            <DialogTitle>Add New Item Tag</DialogTitle>
            <DialogContent>
                <TextField
                    id="Name_textField"
                    label="Name"
                    value={name}
                    onChange={(event) => {
                        setName(event.target.value);
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    id="Confirm"
                    onClick={async () => {
                        await createTag(name);
                        setAddTagModalOpen(false);
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function TagsComponent({ getTags, setTags, tags }) {
    const [addTagModalOpen, setAddTagModalOpen] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });

    const createTag = async (name) => {
        const res = await api.createItemInformationTag(name);
        if (res.status === true) {
            getTags();
        } else {
            setNotification({ text: 'Failed to create tag', level: 'error' });
        }
    };

    const columns = [
        { accessor: 'id', Header: 'id', editable: false },
        { accessor: 'name', Header: 'Name', editable: false },
    ];

    return (
        <Grid container>
            <Notifications options={notification} />
            {addTagModalOpen === true && <AddTagModal createTag={createTag} setAddTagModalOpen={setAddTagModalOpen} />}
            <DataTable
                columns={columns}
                data={tags}
                toolbarActions={[
                    {
                        name: 'Add Tag',
                        action: () => {
                            setAddTagModalOpen(true);
                        },
                    },
                ]}
            />
        </Grid>
    );
}

export { ItemInformation };
